import { useState } from 'react';
import { SimulationConfig, CustomMelody } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { CloudService } from '@/services/CloudService';

export const useFileHandlers = (
  config: SimulationConfig,
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>
) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isUploadingBallSound, setIsUploadingBallSound] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setIsAnalyzing(true); setAnalysisProgress(0);
    try {
      const frequencies = await AUDIO.analyzeFile(file, config.audioCropStart, config.audioCropEnd, (p: number) => setAnalysisProgress(p));
      
      // OPTIMIZATION: Only support .mp3 and .wav for storage
      const uploadResult = await CloudService.uploadFile(file);
      if (!uploadResult) {
        setIsAnalyzing(false);
        setAnalysisProgress(0);
        return;
      }

      const { url: uploadedUrl, size: uploadedSize } = uploadResult;
      const soundId = Math.random().toString(36).substring(7);
      const soundName = file.name.replace(/\.[^/.]+$/, "");
      const newMelody: CustomMelody = {
          id: soundId,
          name: soundName,
          frequencies: frequencies,
          format: 'FREQUENCIES',
          url: uploadedUrl || undefined
      };
      
      setConfig((prev: SimulationConfig) => ({ 
          ...prev, 
          customMelodies: [...prev.customMelodies, newMelody],
          selectedMelody: soundId 
      }));
      
      // Save to cloud melodies table
      await CloudService.registerMelody(newMelody.name, newMelody, uploadedSize);
      
      // Save to cloud songs table (audio)
      if (uploadedUrl) {
          await CloudService.registerSong(newMelody.name, uploadedUrl, undefined, uploadedSize);
          
          // Also register under converted_songs but use the URL instead of base64
          await CloudService.registerConvertedSong(newMelody.name, uploadedUrl);
      }
    } catch (e) { alert("Failed to analyze audio."); }
    setIsAnalyzing(false); setAnalysisProgress(0);
  };

  const handleBallSoundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setIsUploadingBallSound(true);
    const uploadResult = await CloudService.uploadFile(file);
    if (!uploadResult) {
      setIsUploadingBallSound(false);
      return;
    }

    const { url: uploadedUrl, size: uploadedSize } = uploadResult;
    let base64 = "";
    try {
        const reader = new FileReader();
        base64 = await new Promise((resolve) => {
            reader.onload = (ev) => resolve(ev.target?.result as string);
            reader.readAsDataURL(file);
        });
    } catch(e) { console.error("Base64 conversion failed", e); }
    const def = file.name.substring(0, 20).replace(/\.[^/.]+$/, "");
    const name = prompt("Enter sound name:", def);
    if (name === null) { setIsUploadingBallSound(false); return; }
    
    const soundId = Math.random().toString(36).substring(7);
    const soundName = name.trim() || def;

    if (uploadedUrl) {
      await CloudService.registerSound(soundName, uploadedUrl, soundId, uploadedSize);
    }

    setConfig((prev: SimulationConfig) => ({ ...prev, userSounds: [...prev.userSounds, { 
        id: soundId, 
        name: soundName, 
        base64: base64, url: uploadedUrl || undefined 
    }] }));
    setIsUploadingBallSound(false);
  };

  return {
    handleFileUpload,
    handleBallSoundUpload,
    isAnalyzing,
    analysisProgress,
    isUploadingBallSound
  };
};
